import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { useApi } from '~shared/providers/use-api';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Application } from './application';
import type { FindApplicationsRequestParams } from './find-applications-params';

import type { ApplicationsApi } from '../api';
import { APPLICATIONS_API_KEY } from '../api';

export const useFindApplicationsQuery = (
  requestOptions: FindApplicationsRequestParams,
  options: UseQueryOptions<{
    applications: Application[];
    total: number;
  }> = {}
) => {
  const applicationsApi = useApi<ApplicationsApi>(APPLICATIONS_API_KEY);

  return useQuery({
    queryKey: ['find-applications', requestOptions],
    queryFn: async () => {
      const { items, pageInfo } =
        await applicationsApi.findApplications(requestOptions);

      return {
        applications: items,
        total: pageInfo.total,
      };
    },
    keepPreviousData: true,
    ...options,
  });
};

export const useGetApplicationByIdQuery = (applicationId?: string | null) => {
  const applicationsApi = useApi<ApplicationsApi>(APPLICATIONS_API_KEY);

  return useQuery({
    queryKey: ['get-application', applicationId],
    queryFn: async () =>
      applicationsApi.getApplicationById(nonNullableValue(applicationId)),
    enabled: !!applicationId,
  });
};

export const useGenerateReportMutation = () => {
  const applicationsApi = useApi<ApplicationsApi>(APPLICATIONS_API_KEY);

  return useMutation({
    mutationKey: ['generate-report'],
    mutationFn: async (requestParams: FindApplicationsRequestParams) =>
      applicationsApi.generateReport(requestParams),
  });
};
